import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader, EmptyState, ErrorState, StatusPill } from '@ui-kit';
import type { StatusTone } from '@ui-kit';
import { useNotificationHistory } from '../hooks/useNotificationHistory';
import { useNotificationStats } from '../hooks/useNotificationStats';
import { ChannelStatusCards } from '../components/ChannelStatusCards';
import { NotificationHistoryDetailDrawer } from '../components/NotificationHistoryDetailDrawer';
import { NotificationSendDialog } from '../components/NotificationSendDialog';
import { CommonUsagePanel } from '../../_shared/CommonUsagePanel';
import type { CommonUsageItem } from '../../_shared/CommonUsagePanel';
import type {
  NotificationHistoryDto,
  NotificationHistorySearchCondition,
  NotificationStatus,
  NotificationChannel,
  ApiError,
} from '../types/notificationCenter';

const DEFAULT_PAGE_SIZE = 20;

const DEFAULT_FILTERS: NotificationHistorySearchCondition = {
  page: 0,
  size: DEFAULT_PAGE_SIZE,
  sort: 'createdAt,desc',
};

function statusTone(status: NotificationStatus): StatusTone {
  if (status === 'SENT') return 'success';
  if (status === 'PENDING') return 'warning';
  return 'danger';
}

function statusIcon(status: NotificationStatus): string {
  if (status === 'SENT') return '✓';
  if (status === 'PENDING') return '⏳';
  return '✗';
}

function isCriticalChannel(dto: NotificationHistoryDto): boolean {
  // FAILED + WEBHOOK 은 CRITICAL 표시 가능성 높음 — 좌측 빨간 띠
  return dto.status === 'FAILED';
}

function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

/** 테이블 스켈레톤 행 */
function SkeletonRow({ cols }: { cols: number }) {
  return (
    <tr>
      {Array.from({ length: cols }).map((_, i) => (
        <td
          key={i}
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderBottom: '1px solid var(--color-surface-border)',
          }}
        >
          <div
            style={{
              height: 12,
              width: `${50 + ((i * 13) % 40)}%`,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </td>
      ))}
    </tr>
  );
}

/**
 * NotificationCenterPage — 알림 센터 메인 페이지 (/notification-center)
 * ROLE_ADMIN 전용 · NOTIFICATION_READ 권한 필요
 * 5상태: default / loading / empty / error / dialog
 */
export function NotificationCenterPage() {
  const { t } = useTranslation();

  // 필터 상태
  const [pendingChannel, setPendingChannel] = useState<NotificationChannel | ''>('');
  const [pendingStatus, setPendingStatus] = useState<NotificationStatus | ''>('');
  const [pendingFrom, setPendingFrom] = useState('');
  const [pendingTo, setPendingTo] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<NotificationHistorySearchCondition>(DEFAULT_FILTERS);
  const [page, setPage] = useState(0);

  // UI 상태
  const [selectedHistoryId, setSelectedHistoryId] = useState<number | null>(null);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // 데이터 쿼리
  const listQuery = useNotificationHistory(appliedFilters);
  const statsQuery = useNotificationStats();

  const pageData = listQuery.data;

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const handleSearch = useCallback(() => {
    const next: NotificationHistorySearchCondition = {
      page: 0,
      size: DEFAULT_PAGE_SIZE,
      sort: 'createdAt,desc',
    };
    if (pendingChannel) next.channel = pendingChannel;
    if (pendingStatus) next.status = pendingStatus;
    if (pendingFrom) next.from = pendingFrom;
    if (pendingTo) next.to = pendingTo;
    setPage(0);
    setAppliedFilters(next);
  }, [pendingChannel, pendingStatus, pendingFrom, pendingTo]);

  const handleReset = useCallback(() => {
    setPendingChannel('');
    setPendingStatus('');
    setPendingFrom('');
    setPendingTo('');
    setPage(0);
    setAppliedFilters(DEFAULT_FILTERS);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    setAppliedFilters((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleRowClick = useCallback((row: NotificationHistoryDto) => {
    setSelectedHistoryId(row.id);
  }, []);

  const isListLoading = listQuery.isLoading;
  const isListError = listQuery.isError;
  const isEmpty = !isListLoading && !isListError && (pageData?.empty ?? false);
  const showTable = !isListLoading && !isListError && !isEmpty && !!pageData;

  const TABLE_HEADERS = useMemo(() => [
    t('notificationCenter.list.column.createdAt'),
    t('notificationCenter.list.column.channel'),
    t('notificationCenter.list.column.target'),
    t('notificationCenter.list.column.title'),
    t('notificationCenter.list.column.status'),
    t('notificationCenter.list.column.attempts'),
  ], [t]);

  // 공통 모듈 시연 패널 항목 (§6.8)
  const commonUsageItems: CommonUsageItem[] = useMemo(() => [
    {
      module: 'eraf-notification (C077)',
      moduleColor: 'blue',
      api: 'NotificationHistoryService.findHistory(query)',
      role: '발송 이력 목록의 단일 데이터 소스',
      snippet: 'historyService.findHistory(new NotificationHistoryQuery(ch, st, f, t, p))',
    },
    {
      module: 'eraf-notification (C077)',
      moduleColor: 'blue',
      api: 'NotificationHistoryService.resend(id)',
      role: 'FAILED 이력 재발송 (상태머신 위임)',
      snippet: 'historyService.resend(historyId)',
    },
    {
      module: 'eraf-notification (C077)',
      moduleColor: 'blue',
      api: 'NotificationHistoryRecorder.beforeSend/afterSend',
      role: '발송 경로 이력 적재(PENDING) + 상태 전이',
      snippet: 'recorder.beforeSend(ch, masked, title, json); recorder.afterSend(id, result)',
    },
    {
      module: 'eraf-notification (C076)',
      moduleColor: 'green',
      api: 'WebhookChannel.dispatch(WebhookMessage)',
      role: 'Slack/Teams 실 발송',
      snippet: 'webhookChannel.dispatch(WebhookMessage.to(SLACK, title, text, INFO))',
    },
    {
      module: 'eraf-notification (C073)',
      moduleColor: 'green',
      api: 'EmailSender.send(EmailMessage)',
      role: '이메일 실 발송 단일 진입점',
      snippet: 'emailSender.send(EmailMessage.of(to, content))',
    },
    {
      module: 'eraf-core (C013)',
      moduleColor: 'yellow',
      api: 'Masker.maskEmail(to)',
      role: '수신자 평문 미노출',
      snippet: 'masker.maskEmail("ops@skax.com") → "o***@skax.com"',
    },
    {
      module: 'eraf-core (C002)',
      moduleColor: 'yellow',
      api: 'ApiResponse.page(...) / ApiResponse.ok(...)',
      role: '표준 응답 래핑',
      snippet: 'return ApiResponse.page(service.search(q))',
    },
    {
      module: 'eraf-core (C014)',
      moduleColor: 'yellow',
      api: '@Audited(action="NOTIFICATION_SENT")',
      role: '발송·재발송 감사 기록',
      snippet: '@Audited(action="NOTIFICATION_SENT") public ... send(...)',
    },
    {
      module: 'eraf-core (C008)',
      moduleColor: 'yellow',
      api: 'EventPublisher.publish(NotificationSentEvent)',
      role: '발송 완료 이벤트 발행(옵션)',
      snippet: 'eventPublisher.publish(new NotificationSentEvent(...))',
    },
    {
      module: 'eraf-security (C030)',
      moduleColor: 'purple',
      api: '@HasPermission("NOTIFICATION_SEND")',
      role: '발송 권한 (이중 방어)',
      snippet: '@HasPermission("NOTIFICATION_SEND") public ... send(...)',
    },
    {
      module: 'eraf-security (C029)',
      moduleColor: 'purple',
      api: 'SecurityChainCustomizer + PathPolicy',
      role: '/notification-center/** ROLE_ADMIN 정책',
      snippet: 'PathPolicy.hasRole("/api/eraf/notification-center", "ADMIN")',
    },
    {
      module: 'eraf-swagger (C037)',
      moduleColor: 'blue',
      api: '@ApiSuccessResponse(responses=NotificationHistoryDto.class)',
      role: 'OpenAPI 응답 스키마',
      snippet: '(annotation 1줄)',
    },
  ], []);

  return (
    <div style={{ padding: 0 }}>
      {/* 페이지 헤더 */}
      <PageHeader
        title={t('notificationCenter.list.title')}
        breadcrumb={<span>{t('notificationCenter.list.title')}</span>}
        actions={
          <button
            type="button"
            onClick={() => setSendDialogOpen(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('notificationCenter.list.button.send')}
          </button>
        }
      />

      {/* 요약 */}
      {pageData && (
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            margin: 'var(--spacing-2) 0 var(--spacing-4)',
          }}
        >
          {pageData.totalElements > 0
            ? t('notificationCenter.list.totalCount', { count: pageData.totalElements })
            : t('notificationCenter.list.noResults')}
        </p>
      )}

      {/* 채널 통계 카드 */}
      <ChannelStatusCards stats={statsQuery.data} isLoading={statsQuery.isLoading} />

      {/* 필터 바 */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--spacing-3)',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          padding: 'var(--spacing-4)',
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--spacing-4)',
        }}
        role="search"
        aria-label="알림 이력 검색 필터"
      >
        {/* 채널 필터 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', minWidth: 120 }}>
          <label
            htmlFor="filter-channel"
            style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-secondary)' }}
          >
            {t('notificationCenter.list.filter.channel')}
          </label>
          <select
            id="filter-channel"
            value={pendingChannel}
            onChange={(e) => setPendingChannel(e.target.value as NotificationChannel | '')}
            disabled={isListLoading}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              cursor: 'pointer',
              minWidth: 120,
            }}
          >
            <option value="">{t('notificationCenter.list.filter.channelAll')}</option>
            <option value="EMAIL">{t('notificationCenter.channel.EMAIL')}</option>
            <option value="WEBHOOK">{t('notificationCenter.channel.WEBHOOK')}</option>
            <option value="SMS">{t('notificationCenter.channel.SMS')}</option>
            <option value="PUSH">{t('notificationCenter.channel.PUSH')}</option>
          </select>
        </div>

        {/* 상태 필터 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', minWidth: 110 }}>
          <label
            htmlFor="filter-status"
            style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-secondary)' }}
          >
            {t('notificationCenter.list.filter.status')}
          </label>
          <select
            id="filter-status"
            value={pendingStatus}
            onChange={(e) => setPendingStatus(e.target.value as NotificationStatus | '')}
            disabled={isListLoading}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              cursor: 'pointer',
            }}
          >
            <option value="">{t('notificationCenter.list.filter.channelAll')}</option>
            <option value="PENDING">{t('notificationCenter.status.PENDING')}</option>
            <option value="SENT">{t('notificationCenter.status.SENT')}</option>
            <option value="FAILED">{t('notificationCenter.status.FAILED')}</option>
          </select>
        </div>

        {/* 시작일 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
          <label
            htmlFor="filter-from"
            style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-secondary)' }}
          >
            {t('notificationCenter.list.filter.from')}
          </label>
          <input
            id="filter-from"
            type="date"
            value={pendingFrom}
            onChange={(e) => setPendingFrom(e.target.value)}
            disabled={isListLoading}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
            }}
          />
        </div>

        {/* 종료일 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
          <label
            htmlFor="filter-to"
            style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-secondary)' }}
          >
            {t('notificationCenter.list.filter.to')}
          </label>
          <input
            id="filter-to"
            type="date"
            value={pendingTo}
            onChange={(e) => setPendingTo(e.target.value)}
            disabled={isListLoading}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
            }}
          />
        </div>

        {/* 검색 + 초기화 버튼 */}
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'flex-end' }}>
          <button
            type="button"
            onClick={handleSearch}
            disabled={isListLoading}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: isListLoading ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              opacity: isListLoading ? 0.6 : 1,
            }}
          >
            {t('notificationCenter.list.button.search')}
          </button>
          <button
            type="button"
            onClick={handleReset}
            disabled={isListLoading}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              cursor: isListLoading ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('notificationCenter.list.button.reset')}
          </button>
        </div>
      </div>

      {/* 컨텐츠 영역 — 5상태 */}
      <div
        style={{
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          background: 'var(--color-surface-background)',
        }}
      >
        {/* Loading — skeleton */}
        {isListLoading && (
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
            aria-label={t('notificationCenter.list.loadingAriaLabel')}
            aria-busy="true"
          >
            <thead>
              <tr style={{ background: 'var(--color-surface-muted)' }}>
                {TABLE_HEADERS.map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: 'var(--spacing-3) var(--spacing-4)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={i} cols={TABLE_HEADERS.length} />
              ))}
            </tbody>
          </table>
        )}

        {/* Error */}
        {isListError && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <ErrorState
              title={t('notificationCenter.list.error.loadFailed')}
              message={
                (listQuery.error as unknown as ApiError)?.code === 'AUTH-403-001'
                  ? t('notificationCenter.error.AUTH-403-001')
                  : ((listQuery.error as unknown as ApiError)?.message ??
                    t('notificationCenter.error.network'))
              }
              onRetry={() => void listQuery.refetch()}
            />
          </div>
        )}

        {/* Empty */}
        {isEmpty && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <EmptyState
              title={t('notificationCenter.list.empty.title')}
              description={t('notificationCenter.list.empty.description')}
              action={
                <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={handleReset}
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-4)',
                      background: 'var(--color-surface-elevated)',
                      color: 'var(--color-text-primary)',
                      border: '1px solid var(--color-surface-border)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--font-size-sm)',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-family-sans)',
                    }}
                  >
                    {t('notificationCenter.list.button.reset')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSendDialogOpen(true)}
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-4)',
                      background: 'var(--color-brand-primary)',
                      color: 'var(--color-text-inverse)',
                      border: 'none',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'var(--font-family-sans)',
                    }}
                  >
                    {t('notificationCenter.list.empty.cta')}
                  </button>
                </div>
              }
            />
          </div>
        )}

        {/* Default — DataTable */}
        {showTable && pageData && (
          <>
            <div role="region" aria-label={t('notificationCenter.list.tableAriaLabel')} style={{ overflowX: 'auto' }}>
              <table
                style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
                aria-label={t('notificationCenter.list.tableAriaLabel')}
              >
                <thead>
                  <tr style={{ background: 'var(--color-surface-muted)' }}>
                    {TABLE_HEADERS.map((h) => (
                      <th
                        key={h}
                        scope="col"
                        style={{
                          textAlign: 'left',
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-secondary)',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageData.content.map((row) => (
                    <tr
                      key={row.id}
                      tabIndex={0}
                      role="button"
                      aria-label={t('notificationCenter.list.rowAriaLabel', { id: row.id })}
                      onClick={() => handleRowClick(row)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleRowClick(row);
                        }
                      }}
                      style={{
                        cursor: 'pointer',
                        borderLeft: isCriticalChannel(row)
                          ? '3px solid var(--color-danger)'
                          : '3px solid transparent',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background = 'var(--color-surface-muted)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background = '';
                      }}
                    >
                      {/* 시각 */}
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)' }}>
                        <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                          {formatDateTime(row.createdAt)}
                        </span>
                      </td>
                      {/* 채널 */}
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)' }}>
                        <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>
                          {row.channel}
                        </span>
                      </td>
                      {/* 수신자 (마스킹) */}
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)', maxWidth: 200 }}>
                        <span
                          style={{
                            fontFamily: 'var(--font-family-mono)',
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-muted)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                          }}
                          title={row.maskedTarget}
                        >
                          {row.maskedTarget}
                        </span>
                      </td>
                      {/* 제목 */}
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)', maxWidth: 220 }}>
                        <span
                          style={{
                            fontSize: 'var(--font-size-sm)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                          }}
                          title={row.titlePreview ?? '-'}
                        >
                          {row.titlePreview ?? '-'}
                        </span>
                      </td>
                      {/* 상태 */}
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)' }}>
                        <StatusPill tone={statusTone(row.status)}>
                          {statusIcon(row.status)} {t(`notificationCenter.status.${row.status}`)}
                        </StatusPill>
                      </td>
                      {/* 시도 횟수 */}
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)' }}>
                        <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-sm)' }}>
                          {row.attemptCount}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 페이지네이션 */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--spacing-3) var(--spacing-4)',
                background: 'var(--color-surface-muted)',
                borderTop: '1px solid var(--color-surface-border)',
                flexWrap: 'wrap',
                gap: 'var(--spacing-2)',
              }}
            >
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                {t('notificationCenter.list.pagination.showing', {
                  total: pageData.totalElements.toLocaleString(),
                  from: pageData.number * pageData.size + 1,
                  to: Math.min((pageData.number + 1) * pageData.size, pageData.totalElements),
                })}
              </span>
              <nav aria-label={t('notificationCenter.list.pagination.nav')} style={{ display: 'inline-flex', gap: 'var(--spacing-1)' }}>
                <button
                  type="button"
                  disabled={pageData.first}
                  onClick={() => handlePageChange(page - 1)}
                  aria-label={t('notificationCenter.list.pagination.prev')}
                  style={{
                    minWidth: 28, height: 28,
                    padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: pageData.first ? 'not-allowed' : 'pointer',
                    opacity: pageData.first ? 0.5 : 1,
                  }}
                >
                  &lt;
                </button>
                {Array.from({ length: Math.min(pageData.totalPages, 10) }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handlePageChange(i)}
                    aria-label={t('notificationCenter.list.pagination.page', { page: i + 1 })}
                    aria-current={i === pageData.number ? 'page' : undefined}
                    style={{
                      minWidth: 28, height: 28,
                      padding: '0 var(--spacing-2)',
                      borderRadius: 'var(--radius-sm)',
                      background: i === pageData.number ? 'var(--color-brand-primary)' : 'var(--color-surface-elevated)',
                      border: `1px solid ${i === pageData.number ? 'var(--color-brand-primary)' : 'var(--color-surface-border)'}`,
                      color: i === pageData.number ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
                      fontSize: 'var(--font-size-xs)',
                      cursor: 'pointer',
                      fontWeight: i === pageData.number ? 600 : 400,
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={pageData.last}
                  onClick={() => handlePageChange(page + 1)}
                  aria-label={t('notificationCenter.list.pagination.next')}
                  style={{
                    minWidth: 28, height: 28,
                    padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: pageData.last ? 'not-allowed' : 'pointer',
                    opacity: pageData.last ? 0.5 : 1,
                  }}
                >
                  &gt;
                </button>
              </nav>
            </div>
          </>
        )}
      </div>

      {/* 공통 모듈 시연 패널 (§6.8) */}
      <CommonUsagePanel
        items={commonUsageItems}
        title={t('notificationCenter.commonUsage.title')}
        subtitle={t('notificationCenter.commonUsage.description')}
      />

      {/* Drawer — 이력 상세 */}
      <NotificationHistoryDetailDrawer
        historyId={selectedHistoryId}
        onClose={() => setSelectedHistoryId(null)}
        onResendSuccess={(msg) => {
          setSelectedHistoryId(null);
          showToast(msg, 'success');
        }}
        onResendError={(msg) => showToast(msg, 'error')}
      />

      {/* Dialog — 알림 발송 */}
      {sendDialogOpen && (
        <NotificationSendDialog
          onClose={() => setSendDialogOpen(false)}
          onSuccess={(msg) => showToast(msg, 'success')}
          onError={(msg) => showToast(msg, 'error')}
        />
      )}

      {/* 토스트 알림 */}
      {toast !== null && (
        <div
          role="alert"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: 'var(--spacing-6)',
            right: 'var(--spacing-6)',
            zIndex: 1100,
            background:
              toast.type === 'success'
                ? 'var(--color-success)'
                : 'var(--color-danger)',
            color: 'var(--color-text-inverse)',
            padding: 'var(--spacing-3) var(--spacing-5)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            boxShadow: 'var(--shadow-lg)',
            maxWidth: 380,
          }}
        >
          {toast.message}
        </div>
      )}

      {/* 반응형 + 애니메이션 스타일 */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @media (max-width: 768px) {
          table { min-width: 580px; }
        }
      `}</style>
    </div>
  );
}
